/**
 * Frontend env reader. Vite exposes env vars on `import.meta.env` — only the
 * `VITE_*` prefix is bundled, keeping anything secret server-side.
 *
 * Defaults assume the local-dev port plan from BS-005:
 *   - service-mail on http://localhost:3001
 *
 * Validation: hand-rolled (no zod dep — apps/web-staff doesn't ship it and
 * this app honours the same dependency budget). The shape is small enough
 * that a single check is clearer than wiring a runtime schema lib.
 */
const SERVICE_MAIL_DEFAULT = 'http://localhost:3001';
const SERVICE_CUSTOMER_DEFAULT = 'http://localhost:3040';
const SERVICE_PRICING_DEFAULT = 'http://localhost:3030';
const SERVICE_SERVICE_DESK_DEFAULT = 'http://localhost:3140';
const SERVICE_FINANCIAL_SERVICES_DEFAULT = 'http://localhost:3110';
const SERVICE_KYC_DEFAULT = 'http://localhost:3090';
const SERVICE_PAYMENT_DEFAULT = 'http://localhost:3050';
const SERVICE_NOTIFICATION_DEFAULT = 'http://localhost:3020';
const SERVICE_GEO_DEFAULT = 'http://localhost:3170';
const IDENTITY_DEFAULT = 'http://localhost:3013';
const WEB_STAFF_DEFAULT = 'http://localhost:5173';

export interface Env {
  serviceMailBaseUrl: string;
  serviceCustomerBaseUrl: string;
  servicePricingBaseUrl: string;
  serviceServiceDeskBaseUrl: string;
  serviceFinancialServicesBaseUrl: string;
  /** Base URL for `service-kyc` — KYC tier upgrade endpoint. */
  kycBaseUrl: string;
  /** Base URL for `service-payment` — Paystack card-payment endpoints. */
  servicePaymentBaseUrl: string;
  /** Base URL for `service-notification` — citizen NotificationInbox surface. */
  serviceNotificationBaseUrl: string;
  /** Base URL for `service-geo` — countries, states, NG LGAs, NG wards. Powers AddressFields. */
  serviceGeoBaseUrl: string;
  /** Base URL for `service-identity` — login form + 401 refresh interceptor target. */
  identityBaseUrl: string;
  /** Base URL for the staff portal (`web-staff`). The landing page links staff
   *  sign-in to this URL in a new tab. Defaults to the local-dev port. */
  webStaffBaseUrl: string;
  /** Paystack public key (`pk_test_...` / `pk_live_...`). Read at module load
   *  from `VITE_PAYSTACK_PUBLIC_KEY`; defaults to empty string when unset so
   *  CashInPage can gracefully degrade to the deterministic verify path. */
  paystackPublicKey: string;
}

function readBaseUrl(raw: string | undefined, fallback: string): string {
  const value = (raw ?? fallback).trim();
  if (value.length === 0) {
    return fallback;
  }
  // Strip a trailing slash so callers can join with `${base}/v1/track/...`
  // without doubling up.
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

/**
 * Google Maps JavaScript API key. Returned verbatim from `VITE_GOOGLE_MAPS_API_KEY`,
 * or an empty string when the var is unset. UI surfaces (UpmpMap, OutletLocatorPage)
 * check for `''` and render a "Map disabled" notice instead of loading the SDK —
 * keeping demos usable without a key and avoiding Google's loud
 * "InvalidKeyMapError" when one isn't configured.
 *
 * Restrict the key by HTTP referrer in the Google Cloud Console — never ship
 * a wildcard key.
 */
export function googleMapsApiKey(): string {
  return import.meta.env['VITE_GOOGLE_MAPS_API_KEY'] ?? '';
}

export const env: Env = {
  serviceMailBaseUrl: readBaseUrl(
    import.meta.env['VITE_SERVICE_MAIL_BASE_URL'],
    SERVICE_MAIL_DEFAULT,
  ),
  serviceCustomerBaseUrl: readBaseUrl(
    import.meta.env['VITE_SERVICE_CUSTOMER_BASE_URL'],
    SERVICE_CUSTOMER_DEFAULT,
  ),
  servicePricingBaseUrl: readBaseUrl(
    import.meta.env['VITE_SERVICE_PRICING_BASE_URL'],
    SERVICE_PRICING_DEFAULT,
  ),
  serviceServiceDeskBaseUrl: readBaseUrl(
    import.meta.env['VITE_SERVICE_SERVICE_DESK_BASE_URL'],
    SERVICE_SERVICE_DESK_DEFAULT,
  ),
  serviceFinancialServicesBaseUrl: readBaseUrl(
    import.meta.env['VITE_SERVICE_FINANCIAL_SERVICES_BASE_URL'],
    SERVICE_FINANCIAL_SERVICES_DEFAULT,
  ),
  kycBaseUrl: readBaseUrl(
    import.meta.env['VITE_SERVICE_KYC_BASE_URL'],
    SERVICE_KYC_DEFAULT,
  ),
  servicePaymentBaseUrl: readBaseUrl(
    import.meta.env['VITE_SERVICE_PAYMENT_BASE_URL'],
    SERVICE_PAYMENT_DEFAULT,
  ),
  serviceNotificationBaseUrl: readBaseUrl(
    import.meta.env['VITE_SERVICE_NOTIFICATION_BASE_URL'],
    SERVICE_NOTIFICATION_DEFAULT,
  ),
  serviceGeoBaseUrl: readBaseUrl(
    import.meta.env['VITE_SERVICE_GEO_BASE_URL'],
    SERVICE_GEO_DEFAULT,
  ),
  identityBaseUrl: readBaseUrl(
    import.meta.env['VITE_IDENTITY_BASE_URL'],
    IDENTITY_DEFAULT,
  ),
  webStaffBaseUrl: readBaseUrl(
    import.meta.env['VITE_WEB_STAFF_URL'],
    WEB_STAFF_DEFAULT,
  ),
  paystackPublicKey: ((import.meta.env['VITE_PAYSTACK_PUBLIC_KEY'] as string | undefined) ?? '').trim(),
};
